import React, { useEffect, useRef, useState } from "react";
import Environment from "../Environment";
import { useAuth0 } from "@auth0/auth0-react";
import RecurringCallback from "../RecurringCallback";

type OrderData = {
  utid: string;
  driverName: string;
  firstGoodDescription: string;
  purchaseOrderId: string;
};

interface OrderOverviewProps {
  onOrderClick: (utid: string) => void;
}

const OrderOverview: React.FC<OrderOverviewProps> = ({ onOrderClick }) => {
  const { getAccessTokenSilently } = useAuth0();
  const [orders, setOrders] = useState<OrderData[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const ordersForUserUrl = Environment.BuildOrdersforUserEndpoint();

  const orderPolling = useRef<RecurringCallback>(new RecurringCallback());
  
  useEffect(() => {
    if(getAccessTokenSilently.length == 0) return;
    orderPolling.current.stop();
    orderPolling.current.start(fetchOrders, 5000);
  }, [getAccessTokenSilently]);
    
  useEffect(() => {
    // Cleanup on component unmount
    return () => orderPolling.current.stop();
  }, []);

  const fetchOrders = async () => {
    try {
      const token = await getAccessTokenSilently();
      const response = await fetch(ordersForUserUrl, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      if (!response.ok) {
        throw new Error("Failed to fetch UTIDs");
      }

      const utids: string[] = await response.json();

      const orderPromises = utids.map(async (utid) => {
        const orderResponse = await fetch(Environment.BuildOrderJsonEndpoint(utid), {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        });
      
        if (!orderResponse.ok) throw new Error(`Failed to fetch order for ${utid}`);
      
        const orderData = await orderResponse.json();
      
        return {
          utid,
          driverName: orderData.driverName ?? "Unknown",
          firstGoodDescription:
            orderData.consignments?.[0]?.entity?.goods?.[0]?.entity?.description ?? "N/A",
          purchaseOrderId:
            orderData.consignments?.[0]?.entity?.externalAttributes?.purchaseOrderId ?? "N/A",
        };
      });
      
      const resolvedOrders = await Promise.all(orderPromises);
      setOrders(resolvedOrders);
    } catch (err: any) {
      setError(err.message || "An unexpected error occurred.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div>
      {loading && (
        <div className="text-center">
          <div className="spinner-border text-primary" role="status" />
        </div>
      )}

      {error && <div className="alert alert-danger">{error}</div>}

      {!loading && !error && orders.length === 0 && (
        <div className="alert alert-info">You have no orders.</div>
      )}

      {!loading && !error && orders.length > 0 && (
        <table className="table table-striped table-hover table-bordered">
          <thead className="table-light">
            <tr>
            <th scope="col">Purchase Order</th>
            <th scope="col">Driver</th>
            <th scope="col">Description</th>
            </tr>
          </thead>
          <tbody>
            {orders.map((order, index) => (
              <tr
              key={`${order.utid}-${index}`}
              onClick={() => onOrderClick(order.utid)}
              style={{ cursor: "pointer" }}
              >
                <td>{order.purchaseOrderId}</td>
                <td>{order.driverName}</td>
                <td>{order.firstGoodDescription}</td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </div>
  );
};

export default OrderOverview;
